import { Date, Link } from '@cook-types/cook'

export enum GridType {
  FOUR_FOUR_TWO = '4|2|2',
  TWO_TWO_FOUR = '2|2|4',
}

export interface MultilineProps {
  options?: {
    grid?: GridType
    blockType?: string | 'video'
  }
  content?: Record<string, unknown>[]
  kind: string[]
  id?: string
}

export interface ValidData extends Omit<MultilineProps, 'content'> {
  content: Record<string, unknown>[]
}

export interface ValidDataWithNormalContent extends Omit<ValidData, 'content'> {
  content: NormalContent
}

export type NormalContent = (Record<string, unknown> & {
  content: Record<string, unknown>[]
})[]

export type MultilineDossierContent = (Record<string, unknown> & {
  content: Record<string, unknown>[]
})[]

export type MultilineWebarchiveMainContent = {
  kind: ['layout', 'item']
  content: {
    kind: ['layout', 'column', 'webarchive-content']
    title: string
    date: Date
    content: {
      kind: ['layout', 'item']
      content: Record<string, unknown>[]
    }[]
  }[]
}

export type MultilineWebarchiveSidebarContent = {
  kind: ['layout', 'item']
  content: [
    {
      kind: ['layout', 'column', 'webarchive-sidebar']
      content: Record<string, unknown>[]
    }
  ]
}

export type MultilineWebarchiveContent = [
  MultilineWebarchiveMainContent,
  MultilineWebarchiveSidebarContent
]

export type MegaModuleContent = {
  size: 1 | 2 | 3 | 4 | 5
  accentColor: string
  mainTeaser: Record<string, unknown> & {
    content: Record<string, unknown>[]
  }
  additionalTeasers?: (Record<string, unknown> & {
    content: Record<string, unknown>[]
  })[]
}[]

export type MultilineWithPaginationContent = [
  {
    kind: ['layout', 'item']
    content: [
      {
        kind: ['layout', 'column', 'section-content']
        content: Record<string, unknown>[]
      }
    ]
  }
]

export type MultilineArticleGenericMainContent = {
  kind: ['layout', 'column', 'article-content']
  content: {
    kind: string[]
    content: {
      kind: string[]
      content: { kind: string[] }[]
    }[]
  }[]
}

type LottoWidget = {
  kind: ['widget', 'lotto', 'full']
  content: Record<string, unknown>
}

type GenericWidget = {
  kind: string[]
  content: Record<string, unknown>
}

type MultilineArticleLottoContent = [
  {
    kind: ['layout', 'column', 'article-content']
    content: [LottoWidget, ...Array<GenericWidget>]
  }
]

export type MultilineArticleGenericSubcontent = {
  kind: ['layout', 'column', 'article-sub-content']
  content: {
    kind: ['layout', 'multi-line']
    content: {
      kind: string[]
      content: { kind: string[] }[]
    }[]
  }[]
}

export type MultilineHighFiveSubContentItem = {
  kind: ['widget', 'teaser', 'text']
  articleId: number
  description?: string
  link?: Link
  title: string
  updatedDate: Date
}

export type MultilineHighFiveSubContent = MultilineHighFiveSubContentItem[]

export type MultilineHighFiveContent = {
  kind: ['layout', 'item']
  content: MultilineHighFiveSubContent
}[]

export type MultilineArticleGenericContentWithSubcontent = {
  content: [
    MultilineArticleGenericMainContent,
    MultilineArticleGenericSubcontent
  ]
}

export type MultilineArticleGenericContentWithoutSubcontent = {
  content: [MultilineArticleGenericMainContent]
}

export type MultilineArticleSidebarContent = {
  content: [
    {
      kind: ['layout', 'column', 'sidebar']
      content: Record<string, unknown>[]
    }
  ]
}

export type MultilineArticleGenericContent =
  | MultilineArticleGenericContentWithSubcontent
  | MultilineArticleGenericContentWithoutSubcontent

export type MultilineArticleStorytellingContent = [
  MultilineArticleGenericContent
]

export type MultilineArticleStandardContent = [
  MultilineArticleGenericContent,
  MultilineArticleSidebarContent
]

export type MultilineArticleWithoutArticleContent = [
  {
    content: GenericWidget[]
  }
]

export type MultilineArticleContent =
  | MultilineArticleStandardContent
  | MultilineArticleStorytellingContent
  | MultilineArticleLottoContent
  | MultilineArticleWithoutArticleContent

export interface MultilineArticle extends Omit<ValidData, 'content'> {
  content: MultilineArticleContent
}

export interface MultilineMegaModule extends Omit<ValidData, 'content'> {
  content: MegaModuleContent
}

export interface MultilineDossier extends Omit<ValidData, 'content'> {
  content: MultilineDossierContent
}

export interface MultilineWebarchive extends Omit<ValidData, 'content'> {
  content: MultilineWebarchiveContent
}

export interface MultilineWithPagination extends Omit<ValidData, 'content'> {
  kind: string[]
  content: MultilineWithPaginationContent
}

export interface MultilineGridData extends Omit<ValidData, 'content'> {
  content: NormalContent
  options: ValidData['options'] & {
    grid: GridType
  }
}

export interface MultilineHighFive extends Omit<ValidData, 'content'> {
  content: MultilineHighFiveContent
}
